/**
 * The "an AI agent is working on this card right now" store.
 *
 * A module-level external store (not React state, not the query cache):
 * the glow has to render on cards on EVERY board surface and in the card
 * modal at once, fed by three independent sources — the board / dashboard /
 * single-card payloads (a tab opened mid-run still glows) and the
 * `agent.working` WebSocket frames (start / stop while the tab is open).
 * A cache entry per surface would put the same fact in several places that
 * have to be kept in sync; one keyed map has a single writer per source and
 * N cheap readers.
 *
 * Invariants that are easy to break:
 *
 * - **Every entry is a lease, not a fact.** It expires `AGENT_WORKING_TTL_MS`
 *   after the last time the server confirmed it (a start frame or a payload
 *   that listed it). A lost stop frame — a tab that slept through it, a
 *   socket that reconnected — must not leave a card glowing forever. The
 *   lease runs from RECEIPT, not from the payload's `since`: a run that has
 *   legitimately been going for twelve minutes is still live when a
 *   freshly-loaded payload lists it, and the client clock is not trusted to
 *   agree with the server's.
 * - **A payload is a snapshot, a frame is an event.** A snapshot that omits
 *   an agent removes it, but only if the entry was received BEFORE the
 *   request was made — an entry that arrived while the request was in flight
 *   is newer than the snapshot and must survive. The mirror case is a stop
 *   frame landing mid-request: it leaves a short tombstone so the stale
 *   snapshot that still lists the agent cannot resurrect the glow.
 * - **`getAgents` returns a stable array** until the card's set changes, so
 *   `useSyncExternalStore` consumers don't re-render on unrelated changes.
 */

export interface WorkingAgent {
  readonly agentUserId: string
  readonly agentName: string
  /** ISO timestamp the agent started working on the card. */
  readonly since: string
}

/** Wire shape of `agent.working` frame payloads. */
export interface AgentWorkingFramePayload {
  readonly cardKind?: string
  readonly cardId: string
  readonly projectId?: string | null
  readonly agentUserId: string
  readonly agentName: string
  readonly state: 'start' | 'stop'
  readonly at?: string
}

/** A card-shaped payload row that may carry the bootstrap field. */
export interface SeedableCard {
  readonly id: string
  readonly workingAgents?: readonly WorkingAgent[] | null
}

/**
 * How long an entry lives without a fresh confirmation: 17 minutes, one
 * minute past the server's 16-minute stale-run cutoff. A lost stop frame
 * therefore clears within 17 minutes, while a live run is re-leased by its
 * frames and by every payload that lists it (bootstrap), so it never lapses.
 */
export const AGENT_WORKING_TTL_MS = 17 * 60 * 1000

/** How long a stop frame blocks an older snapshot from re-adding the agent. */
const TOMBSTONE_MS = 60 * 1000

const EMPTY: readonly WorkingAgent[] = Object.freeze([])

interface Entry {
  agentUserId: string
  agentName: string
  since: string
  /** Local receipt time of the confirmation that last refreshed this lease. */
  receivedAt: number
  timer: ReturnType<typeof setTimeout>
}

const tombstoneKey = (cardId: string, agentUserId: string) => `${cardId}\u0000${agentUserId}`

export class AgentWorkingStore {
  private readonly byCard = new Map<string, Map<string, Entry>>()
  private readonly snapshots = new Map<string, readonly WorkingAgent[]>()
  private readonly tombstones = new Map<string, number>()
  private readonly listeners = new Set<() => void>()

  constructor(private readonly now: () => number = () => Date.now()) {}

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  /** Stable until the card's agent set changes. */
  getAgents = (cardId: string | null | undefined): readonly WorkingAgent[] => {
    if (!cardId) return EMPTY
    return this.snapshots.get(cardId) ?? EMPTY
  }

  /**
   * Apply a list payload (board / dashboard / single card). `requestedAt` is
   * the local time the request was SENT — entries confirmed after it are
   * newer than the snapshot and are left alone.
   */
  seed(cards: ReadonlyArray<SeedableCard>, requestedAt: number = this.now()): void {
    const now = this.now()
    this.pruneTombstones(now)
    const touched = new Set<string>()

    for (const card of cards) {
      const listed = card.workingAgents ?? []
      const listedIds = new Set<string>()

      for (const agent of listed) {
        listedIds.add(agent.agentUserId)
        const stoppedAt = this.tombstones.get(tombstoneKey(card.id, agent.agentUserId))
        if (stoppedAt !== undefined && stoppedAt > requestedAt) continue
        const existing = this.byCard.get(card.id)?.get(agent.agentUserId)
        // A start frame that arrived after this request was sent is newer
        // than the snapshot's view of the same agent — keep it as is.
        if (existing && existing.receivedAt > requestedAt) continue
        if (this.upsert(card.id, agent, now)) touched.add(card.id)
      }

      const current = this.byCard.get(card.id)
      if (current) {
        for (const [agentUserId, entry] of [...current]) {
          if (listedIds.has(agentUserId)) continue
          if (entry.receivedAt > requestedAt) continue
          if (this.remove(card.id, agentUserId)) touched.add(card.id)
        }
      }
    }

    this.commit(touched)
  }

  /** Apply one `agent.working` frame. */
  applyFrame(payload: AgentWorkingFramePayload): void {
    const { cardId, agentUserId } = payload
    if (!cardId || !agentUserId) return
    const now = this.now()
    if (payload.state === 'stop') {
      this.tombstones.set(tombstoneKey(cardId, agentUserId), now)
      if (this.remove(cardId, agentUserId)) this.commit([cardId])
      return
    }
    if (payload.state !== 'start') return
    this.tombstones.delete(tombstoneKey(cardId, agentUserId))
    const since = payload.at ?? new Date(now).toISOString()
    const existingSince = this.byCard.get(cardId)?.get(agentUserId)?.since
    // A repeat start keeps the original `since` (it orders the names in the
    // label) and just renews the lease.
    const changed = this.upsert(
      cardId,
      { agentUserId, agentName: payload.agentName, since: existingSince ?? since },
      now,
    )
    if (changed) this.commit([cardId])
  }

  /**
   * Drop every entry for one agent, on every card. An agent runs one turn at
   * a time, so the end of its turn ends all of its working leases at once —
   * even when the per-card stop frames never arrived. Leaves a tombstone per
   * card so an older snapshot still listing the agent cannot bring the glow
   * back. Returns the cards that were cleared.
   */
  clearAgent(agentUserId: string): string[] {
    if (!agentUserId) return []
    const now = this.now()
    const cleared: string[] = []
    for (const cardId of [...this.byCard.keys()]) {
      if (this.remove(cardId, agentUserId)) {
        this.tombstones.set(tombstoneKey(cardId, agentUserId), now)
        cleared.push(cardId)
      }
    }
    this.commit(cleared)
    return cleared
  }

  /** Drop everything (sign-out, deactivation, tests). */
  clear(): void {
    const touched = [...this.byCard.keys()]
    for (const entries of this.byCard.values()) {
      for (const entry of entries.values()) clearTimeout(entry.timer)
    }
    this.byCard.clear()
    this.tombstones.clear()
    this.commit(touched)
  }

  // -- internals ----------------------------------------------------------

  /** Returns true when the card's VISIBLE set changed (not for a pure lease renewal). */
  private upsert(cardId: string, agent: WorkingAgent, now: number): boolean {
    let entries = this.byCard.get(cardId)
    if (!entries) {
      entries = new Map()
      this.byCard.set(cardId, entries)
    }
    const existing = entries.get(agent.agentUserId)
    if (existing) clearTimeout(existing.timer)
    const timer = setTimeout(() => {
      if (this.remove(cardId, agent.agentUserId)) this.commit([cardId])
    }, AGENT_WORKING_TTL_MS)
    entries.set(agent.agentUserId, {
      agentUserId: agent.agentUserId,
      agentName: agent.agentName,
      since: agent.since,
      receivedAt: now,
      timer,
    })
    return !existing || existing.agentName !== agent.agentName || existing.since !== agent.since
  }

  private remove(cardId: string, agentUserId: string): boolean {
    const entries = this.byCard.get(cardId)
    const entry = entries?.get(agentUserId)
    if (!entries || !entry) return false
    clearTimeout(entry.timer)
    entries.delete(agentUserId)
    if (entries.size === 0) this.byCard.delete(cardId)
    return true
  }

  private commit(cardIds: Iterable<string>): void {
    let any = false
    for (const cardId of cardIds) {
      const entries = this.byCard.get(cardId)
      if (!entries || entries.size === 0) {
        if (this.snapshots.delete(cardId)) any = true
        continue
      }
      const next = [...entries.values()]
        .sort((a, b) =>
          a.since < b.since ? -1 : a.since > b.since ? 1 : a.agentUserId < b.agentUserId ? -1 : 1,
        )
        .map((e) => ({ agentUserId: e.agentUserId, agentName: e.agentName, since: e.since }))
      const prev = this.snapshots.get(cardId)
      if (
        prev &&
        prev.length === next.length &&
        prev.every(
          (p, i) =>
            p.agentUserId === next[i]?.agentUserId &&
            p.agentName === next[i]?.agentName &&
            p.since === next[i]?.since,
        )
      ) {
        continue
      }
      this.snapshots.set(cardId, next)
      any = true
    }
    if (any) for (const listener of [...this.listeners]) listener()
  }

  private pruneTombstones(now: number): void {
    for (const [key, at] of this.tombstones) {
      if (now - at > TOMBSTONE_MS) this.tombstones.delete(key)
    }
  }
}

/** The app-wide instance every hook and surface reads. */
export const agentWorkingStore = new AgentWorkingStore()
